import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { TooltipDirective } from '../../../../../../common/directives/tooltip/tooltip.directive';
import { SubmittedPostStatusEnum } from '../../../../../../common/general-types/submitted-posts.dto';
import {
  channelBrandColour,
  channelMark,
  crossPlatformBadge,
  crossPlatformBadgeColour,
  CrossPlatformState,
  postStatusDetail,
  postStatusSeverity,
} from '../../cross-platform-state.util';

/**
 * One channel tile in the Cross-posts column.
 *
 * Every state is carried by shape as well as colour — solid vs dashed border, tick vs
 * slash vs clock vs warning vs X badge — so the column stays readable without relying
 * on colour alone. Platform brand colours are used only as tile fills for included/live
 * states; they are not part of the UI palette.
 */
@Component({
  selector: 'mbt-cross-platform-tile',
  imports: [TooltipDirective],
  templateUrl: './cross-platform-tile.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CrossPlatformTile {
  /** SocialChannelEnum value this tile represents. */
  readonly socialChannel = input.required<number>();
  readonly state = input.required<CrossPlatformState>();
  /** Human label, e.g. "Instagram Story". */
  readonly label = input<string>('');
  /** Brand cost of this cross-post for this creator; omitted when not priced. */
  readonly price = input<number | null>(null);
  readonly currencyCode = input<string>('GBP');
  /** Only put-forward channels on a live applicant are clickable. */
  readonly interactive = input<boolean>(false);
  /** Submitted-post status for this channel, when a post exists. */
  readonly postStatus = input<SubmittedPostStatusEnum | string | null>(null);

  /**
   * Replaces the derived tooltip. The Primary post column needs this: the primary
   * channel reuses the muted tile for "not hired yet", but the derived wording
   * ("not put forward") is meaningless for a channel the brief requires.
   */
  readonly tooltipOverride = input<string | null>(null);

  readonly toggled = output<number>();

  /** Two-letter mark, standing in for a brand glyph. */
  readonly mark = computed(() => channelMark(this.socialChannel()));

  /** Brand fill, used only when the tile is solid. */
  readonly brandColour = computed(() => channelBrandColour(this.socialChannel()));

  /** Solid brand fill for live and included tiles; white otherwise. */
  readonly isSolid = computed(
    () => this.state() === 'posted' || this.state() === 'assigned',
  );

  readonly isMuted = computed(() => {
    const state = this.state();
    return state === 'none' || state === 'locked';
  });

  /** Put forward but currently switched off — must read as re-enableable, not disabled. */
  readonly isExcluded = computed(() => this.state() === 'excluded');

  readonly severity = computed(() => postStatusSeverity(this.postStatus()));

  readonly badge = computed(() => crossPlatformBadge(this.state(), this.postStatus()));

  readonly badgeColour = computed(() =>
    crossPlatformBadgeColour(this.badge(), this.severity()),
  );

  readonly tooltip = computed(() => {
    const override = this.tooltipOverride();
    if (override) {
      return override;
    }

    const label = this.label();
    const price = this.price();
    const money = price != null && price > 0 ? ` · ${this.formatPrice(price)}` : '';
    const action = this.interactive()
      ? this.state() === 'excluded'
        ? ' · click to include'
        : ' · click to exclude'
      : '';
    const statusNote = this.statusTooltipNote();

    switch (this.state()) {
      case 'posted':
        return `${label} — cross-posted`;
      case 'assigned':
        return `${label} — included in hire${money}${action}${statusNote}`;
      case 'proposed':
        return `${label} — put forward by creator${money}${action}${statusNote}`;
      case 'excluded':
        return `${label} — put forward, not included${money}${action}${statusNote}`;
      case 'none':
        return `${label} — creator has no connected account`;
      case 'locked':
      default:
        return `${label} — not put forward`;
    }
  });

  onToggle(): void {
    if (!this.interactive()) {
      return;
    }
    this.toggled.emit(this.socialChannel());
  }

  private statusTooltipNote(): string {
    const detail = postStatusDetail(this.postStatus());
    return detail ? ` · ${detail}` : '';
  }

  private formatPrice(price: number): string {
    // Mirrors the CurrencyPipe used in the template, for the tooltip string.
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: this.currencyCode() || 'GBP',
    }).format(price);
  }
}
